/**
 * What a storage policy means, in words somebody can act on.
 *
 * A bucket decides who may read a customer's files. Every mistake available
 * here is quiet: a bucket made public because "public" sounded like
 * "shared with my app", a bucket deleted in the belief it removes the
 * files, a leaked link nobody realises is still live for another six days.
 *
 * So the page's job is not a form. It is saying, before each action, what
 * that action will actually do.
 */

import type { StorageBucket } from './api.js'

export type Policy = { visibility: 'private' | 'public'; ownerScoped: boolean }

/**
 * What this bucket allows, said as consequences rather than settings.
 *
 * Reading a row of checkboxes tells somebody what they ticked. It does not
 * tell them that anybody with a URL can now open their users' uploads.
 */
export function consequences(policy: Policy): string[] {
  const lines: string[] = []

  if (policy.visibility === 'public') {
    lines.push('Anyone with a file’s URL can read it — no key, no sign-in.')
    // The two halves people assume wrongly in opposite directions.
    lines.push('They cannot list what is in the bucket, and cannot write to it.')
  } else {
    lines.push('Nobody can read a file without a service key or a signed URL.')
  }

  if (policy.ownerScoped) {
    lines.push('A signed-in user reads and writes their own folder — <bucket>/<their user id>/… — '
      + 'and nothing else. This is what lets your app upload without a service key.')
  }

  if (policy.visibility === 'private' && !policy.ownerScoped) {
    lines.push('Your app cannot reach this bucket directly. Make it public, or turn on per-user '
      + 'folders, or have your server hand out signed URLs.')
  }

  // Said on every bucket, every time. The belief that a bucket confines a
  // service key is what ends with one in a browser.
  lines.push('A service key still reads and writes this bucket, and everything outside it.')
  return lines
}

/** A one-line summary for a list. */
export const summarise = (bucket: StorageBucket): string =>
  [bucket.visibility, bucket.ownerScoped ? 'per-user folders' : null].filter(Boolean).join(' · ')

/**
 * Whether saving this change will kill links already handed out.
 *
 * Public becoming private is a withdrawal, and the server revokes on it.
 * Somebody should know that before they press save, not after a support
 * ticket about a broken image.
 */
export const willRevoke = (from: StorageBucket | null, to: Policy): boolean =>
  from?.visibility === 'public' && to.visibility === 'private'

export const REVOKE_CONSEQUENCES = [
  'Every signed URL for this bucket stops working immediately.',
  'Links you have emailed, embedded or shared are included — there is no undo.',
  'The files are untouched, and new signed URLs work normally.',
]

export const DELETE_CONSEQUENCES = [
  'The files are NOT deleted. They stay exactly where they are.',
  'They stop being reachable with an anon key or a signed URL, so an app reading them breaks.',
  'A service key still reads them, and you can register the bucket again.',
]

export type NameProblem = string | null

/**
 * A name that will survive being the first segment of every key in it.
 *
 * Checked here as well as at the API because a 400 carrying a regex is a
 * worse way to learn that capitals are not allowed than a sentence under
 * the box.
 */
export function checkName(raw: string): NameProblem {
  const name = raw.trim()
  if (name === '') return 'A name is needed.'
  if (name.length > 63) return 'Up to 63 characters.'
  if (name !== name.toLowerCase()) return 'Lowercase only — it becomes part of every file’s path.'
  if (!/^[a-z0-9][a-z0-9_-]*$/.test(name)) {
    return 'Letters, digits, dash and underscore, starting with a letter or digit.'
  }
  return null
}

/**
 * What to say when the API refuses because files are already there.
 *
 * The server refuses once on purpose. The page's job is to turn that into
 * the choice it actually is, without hiding what the choice does.
 */
export const ADOPT_PROMPT = 'Files already exist under this name. Registering it applies this policy '
  + 'to them. Nothing is moved or deleted, and you can remove the bucket again afterwards.'
